import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from './SEOHead';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Show nothing while auth is loading to prevent premature redirects
  if (loading) {
    return null; // The StartupLoadingScreen will handle the loading display
  }

  // Check if this is a bot/crawler to avoid redirecting search engines
  const isBot = typeof window !== 'undefined' && /bot|crawler|spider|crawling/i.test(navigator.userAgent);

  if (!currentUser && !isBot) {
    // Redirect to login with the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // For bots/crawlers without auth, show a placeholder or basic content structure
  if (!currentUser && isBot) {
    return (
      <>
        <SEOHead 
          title="Protected Content - ClipThis"
          description="This content requires authentication to view."
        />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Protected Content</h1>
            <p className="text-gray-600">This content requires authentication to view.</p>
          </div>
        </div>
      </>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute; 